// Product analytics for the PRO+ video options. Two events, both fired on a deliberate tap, so the
// volume is bounded by how often people open the format step — not by renders or by polling.
//
// They answer the two questions the feature will actually be judged on: do PRO+ members use the
// options (and which ones), and does the locked row convert. The render side is already covered by
// Flyover::RenderTelemetry, which now carries the same three flags.
//
// Names and wire values are a contract with the apps' own copies
// (`FlyoverVideoOptionsAnalytics.kt`, `FlyoverVideoOptionsAnalyticsEvent.swift`): all three feed one
// PostHog project, so a drifting string splits a breakdown rather than failing anywhere.

import type { FlyoverVideoOptionKey } from './videoOptions';
import { shareKind, normalizeShareSource, type AnalyticsShareConfig } from './shareAnalytics';

export const VIDEO_OPTION_CHANGED_EVENT = 'Flyover Options Changed';
export const VIDEO_OPTIONS_PAYWALL_EVENT = 'Flyover Options Paywall Opened';

/** Option key → wire value. snake_case, matching the GraphQL input fields and the apps. */
export const VIDEO_OPTION_WIRE: Record<FlyoverVideoOptionKey, string> = {
  hideLogo: 'hide_logo',
  hideStats: 'hide_stats',
  skipIntro: 'skip_intro',
  hidePhotos: 'hide_photos',
};

export interface VideoOptionChangedProps {
  option: string;
  /** The value it was switched TO, so "turned on" and "turned off again" are separable. */
  enabled: boolean;
  /** How many of the three are on AFTER the change — the bare-bones funnel in one property. */
  removed_count: number;
  kind: 'flyover' | 'trailer';
  source: string;
}

export function videoOptionChangedProps(
  cfg: AnalyticsShareConfig | null | undefined,
  option: FlyoverVideoOptionKey,
  enabled: boolean,
  removedCount: number,
): VideoOptionChangedProps {
  return {
    option: VIDEO_OPTION_WIRE[option],
    enabled,
    removed_count: removedCount,
    kind: shareKind(cfg),
    source: normalizeShareSource(cfg?.source),
  };
}

export interface VideoOptionsPaywallProps {
  /** Which locked row was tapped — which promise actually pulls people to the paywall. */
  option: string;
  kind: 'flyover' | 'trailer';
  source: string;
}

export function videoOptionsPaywallProps(
  cfg: AnalyticsShareConfig | null | undefined,
  option: FlyoverVideoOptionKey,
): VideoOptionsPaywallProps {
  return {
    option: VIDEO_OPTION_WIRE[option],
    kind: shareKind(cfg),
    source: normalizeShareSource(cfg?.source),
  };
}

/** Fire-and-forget; analytics must never break the share. */
export function trackVideoOptionChanged(
  cfg: AnalyticsShareConfig | null | undefined,
  option: FlyoverVideoOptionKey,
  enabled: boolean,
  removedCount: number,
): void {
  try {
    window.mixpanel?.track(
      VIDEO_OPTION_CHANGED_EVENT,
      videoOptionChangedProps(cfg, option, enabled, removedCount),
    );
  } catch {
    /* analytics must never break the share */
  }
}

export function trackVideoOptionsPaywall(
  cfg: AnalyticsShareConfig | null | undefined,
  option: FlyoverVideoOptionKey,
): void {
  try {
    window.mixpanel?.track(VIDEO_OPTIONS_PAYWALL_EVENT, videoOptionsPaywallProps(cfg, option));
  } catch {
    /* analytics must never break the share */
  }
}
